// ── Server-only ops (require online) ───────────────────────────────────────────
import {
  companySettleOrder,
  createCompanyClient,
  createTakeAwayOrder,
  directSettleOrder,
  getOrCreateTableOrder,
  otherSettleOrder,
  shiftConfirmedOrderItemsToTable,
  shiftConfirmedOrderItemToTable,
  splitSettleOrder,
} from '@/renderer/services/orderService';

// ── All reads + Category A writes go through local SQLite (PowerSync) ─────────
import {
  isOnline,
  localAddOrderDiscount,
  localAddOrderItem,
  localAssignClientToOrder,
  localConfirmOrderKot,
  localCreateTakeawayOrder,
  localCreditSettleOrder,
  localFetchClients,
  localFetchOpenTakeAwayOrders,
  localFetchOrderById,
  localFetchOrderItems,
  localFetchPaymentMethods,
  localFetchRunningTableOrder,
  localFetchWaiters,
  localGetDepartmentKotEnabled,
  localGetOrCreateTableOrder,
  localInsertRpcOrder,
  LocalOrderContext,
  localOtherSettleOrder,
  localRemoveOrderDiscount,
  localRemoveOrderItem,
  localSeedRpcSettlement,
  localSetOrderBillPrinted,
  localSettleOrder,
  localShiftOrderToTable,
  localSplitSettleOrder,
  localUpdateOrderItemNotes,
  localUpdateOrderWaiter,
  UNAVAILABLE_ITEM_NAME,
} from '@/renderer/lib/powersync/localOrderOps';
import {
  localCheckOrderFiscalReadiness,
  localFetchLatestSettlement,
} from '@/renderer/lib/powersync/localEbmOps';
import { openNewDay as openNewDayService } from '@/renderer/services/businessDayService';
import {
  printBillTicket,
  printKotTicket,
} from '@/renderer/services/printerService';
import {
  exportSalesReportPdf,
  fetchOutletSalesReport,
  sendSalesReportEmail,
} from '@/renderer/services/salesReportService';
import { OpenNewDayResult } from '@/renderer/types/businessDay';
import { MenuItem } from '@/renderer/types/menu';
import { SalesOrder, SalesOrderItem } from '@/renderer/types/order';
import {
  BillPrintTicketClient,
  OutletPrinter,
  PrinterOperation,
} from '@/renderer/types/printer';
import { OutletSalesReport } from '@/renderer/types/report';
import { isElectron } from '@/renderer/util';
import { apiError, apiInfo, apiSuccess } from '@/renderer/util/notify';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AccessControlCode, FULL_ACCESS_USER_ROLES } from '@shared/constants';
import { ApiResponse, BusinessType } from '@shared/types';
import { fetchCurrentUser } from '../auth/authThunks';
import { fetchTables } from '../sections/sectionThunks';
import {
  AddOrderDiscountPayload,
  AddOrderItemPayload,
  AssignClientPayload,
  CreateClientForSelectedOrderPayload,
  CreditSettlementPayload,
  DirectSettlementPayload,
  OtherSettlementPayload,
  RemoveOrderItemPayload,
  ReprintKotPayload,
  RunningOrderClientAssignResult,
  RunningOrderClientModalOpenResult,
  RunningOrderCreateResult,
  RunningOrderKotConfirmResult,
  RunningOrderSelectionResult,
  RunningOrderSettleOpenResult,
  RunningOrderShiftResult,
  RunningOrderTakeAwayOrderResult,
  RunningOrderThunkState,
  RunningOrderWaiterAssignResult,
  RunningOrderWaiterModalOpenResult,
  SelectedTable,
  SettlementReceiptContext,
  ShiftOrderItemPayload,
  ShiftOrderItemsPayload,
  ShiftOrderPayload,
  SplitSettlementPayload,
  UpdateOrderItemNotesPayload,
  UpdateOrderWaiterPayload,
} from './runningOrderTypes';

const getPrintedByName = (state: RunningOrderThunkState): string => {
  const { user } = state.auth;
  if (!user) return 'System';

  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return fullName || user.email || 'System';
};

const isTakeAwayLikeOrder = (order: SalesOrder): boolean =>
  order.mode !== 'DINE';

const isRestaurantOutlet = (state: RunningOrderThunkState): boolean =>
  state.auth.user?.outlet?.business_type === BusinessType.RESTAURANT_BAR;

const hasAccessControl = (
  state: RunningOrderThunkState,
  control: AccessControlCode,
): boolean => {
  const userRole = state.auth.user?.role;

  if (userRole && FULL_ACCESS_USER_ROLES.includes(userRole)) {
    return true;
  }

  return state.auth.accessControlCodes.includes(control);
};

const getLocalBusinessDateString = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Build the LocalOrderContext from Redux auth state.
 * Passed to every local SQLite operation that needs outlet/user details.
 */
const buildLocalCtx = (state: RunningOrderThunkState): LocalOrderContext => {
  const { user } = state.auth;
  const firstName = user?.first_name || '';
  const lastName = user?.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim() || user?.email || 'Staff';

  return {
    outletId: user?.outlet?.id || '',
    outletName: user?.outlet?.name || '',
    companyId: user?.company?.id ?? 0,
    userId: user?.id || '',
    userName: fullName,
    isKdsEnabled: Boolean(user?.outlet?.is_kds_enabled),
  };
};

const formatBusinessDateForMessage = (value: string): string => {
  const parsedDate = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const resolveSectionPrinter = (
  state: RunningOrderThunkState,
  sectionId: number,
  operation: Exclude<PrinterOperation, 'KOT'>,
):
  | {
      success: true;
      printer: OutletPrinter;
    }
  | {
      success: false;
      error: string;
    } => {
  const mapping = state.printers.sectionMappings.find(
    (entry) =>
      entry.section_id === sectionId &&
      entry.operation === operation &&
      !entry.department_id &&
      Boolean(entry.printer_id),
  );

  if (!mapping) {
    return {
      success: false,
      error: `No ${operation} printer is configured for this section`,
    };
  }

  const printer = state.printers.outletPrinters.find(
    (entry) => entry.id === mapping.printer_id && entry.is_active,
  );

  if (!printer) {
    return {
      success: false,
      error: `Configured ${operation} printer is missing or inactive`,
    };
  }

  return {
    success: true,
    printer,
  };
};

const resolveTakeAwayPrinter = (
  state: RunningOrderThunkState,
  operation: Exclude<PrinterOperation, 'KOT'>,
):
  | {
      success: true;
      printer: OutletPrinter;
    }
  | {
      success: false;
      error: string;
    } => {
  const { deviceId, takeAwayMappings } = state.printers;
  if (!deviceId) {
    return {
      success: false,
      error: 'Current device ID is not available for takeaway printing',
    };
  }

  const mapping = takeAwayMappings.find(
    (entry) =>
      entry.device_id === deviceId &&
      entry.operation === operation &&
      !entry.department_id &&
      Boolean(entry.printer_id),
  );

  if (!mapping) {
    return {
      success: false,
      error: `No ${operation} printer is configured for takeaway orders on this device`,
    };
  }

  const printer = state.printers.outletPrinters.find(
    (entry) => entry.id === mapping.printer_id && entry.is_active,
  );

  if (!printer) {
    return {
      success: false,
      error: `Configured takeaway ${operation} printer is missing or inactive`,
    };
  }

  return {
    success: true,
    printer,
  };
};

const getBillLineItems = (items: SalesOrderItem[]) => {
  const grouped = items
    .filter((item) => item.status !== 'CANCELLED')
    .reduce<Map<string, { name: string; quantity: number; amount: number }>>(
      (acc, item) => {
        const key = `${item.menu_item_id}:${item.unit_price}`;
        const existing = acc.get(key);
        if (existing) {
          existing.quantity += item.quantity;
          existing.amount += item.amount;
          return acc;
        }

        acc.set(key, {
          name: item.name,
          quantity: item.quantity,
          amount: item.amount,
        });
        return acc;
      },
      new Map(),
    );

  return Array.from(grouped.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
};

/** Resolve a payment method's display name for a settlement receipt line. */
const resolveSettlementDisplayName = (
  state: RunningOrderThunkState,
  paymentMethodId: number,
): string =>
  state.runningOrder.settlementPaymentMethods.find(
    (pm) => pm.id === paymentMethodId,
  )?.name ?? 'Payment';

/** Resolve the credit payment method's display name, matching the same
 * UPPER(name) IN (...) lookup localCreditSettleOrder uses server-side. */
const resolveCreditSettlementDisplayName = (
  state: RunningOrderThunkState,
): string =>
  state.runningOrder.settlementPaymentMethods.find((pm) =>
    ['CREDIT BILL', 'CREDIT', 'COMPANY CREDIT'].includes(pm.name.toUpperCase()),
  )?.name ?? 'Credit';

/**
 * Blocking fiscal-readiness gate — called at the top of all 4 settlement
 * thunks, before any RPC/local settlement call. Returns an error message if
 * the order should NOT be allowed to settle (an EBM-active outlet with a
 * line item missing fiscal_tax_category_id/ebm_item_cd), or null if it's
 * fine to proceed. Fully local/offline — reads the cached outlet
 * is_ebm_active flag first so this never round-trips for non-fiscal
 * outlets. Must NOT use company.has_fiscal_integration: that is
 * country-level and true for every Rwandan outlet, even ones that never
 * set up EBM.
 */
const assertFiscalReadiness = async (
  state: RunningOrderThunkState,
  orderId: string,
): Promise<string | null> => {
  if (!state.auth.user?.outlet?.is_ebm_active) return null;
  const readiness = await localCheckOrderFiscalReadiness(orderId);
  if (!readiness.success || !readiness.data || readiness.data.length === 0) {
    return null;
  }
  const names = readiness.data.map((item) => item.menu_item_name).join(', ');
  return `Cannot settle — ${readiness.data.length} item(s) missing fiscal setup (${names}). Ask an admin to fix this from Menu Items.`;
};

/**
 * Submits the just-settled order to EBM, once fiscal readiness (above) has
 * already guaranteed every line item is classified. Called AFTER settlement
 * has already committed — a post-settlement EBM failure does not roll back
 * or fail the settlement thunk itself (the sale already happened), it's
 * surfaced separately so the cashier/manager can retry or override.
 *
 * Interim UX: failures surface as a toast for now. A dedicated
 * retry/override modal (gated on SALES_EBM_OVERRIDE_UNCONFIRMED) is planned
 * but not yet built — see the EBM integration plan's §3.5.
 */
const submitSettledSaleToEbm = async (
  state: RunningOrderThunkState,
  orderId: string,
): Promise<void> => {
  if (!state.auth.user?.outlet?.is_ebm_active) return;
  const outletId = state.auth.user?.outlet?.id;
  if (!outletId || !isElectron()) return;

  const settlement = await localFetchLatestSettlement(orderId);
  if (!settlement.success || !settlement.data) return;

  const { user } = state.auth;
  const registrantName = user
    ? `${user.first_name} ${user.last_name}`.trim() || user.email
    : 'POS';

  try {
    const result = await window.electron.ipcRenderer.invoke('ebm:submitSale', {
      outletId,
      orderId,
      settlementId: settlement.data.id,
      paymentMethodId: settlement.data.payment_method_id,
      registrantId: user?.id ?? 'POS',
      registrantName,
    });
    const response = result as
      | ApiResponse<{ status: string; errorMessage?: string }>
      | undefined;
    if (!response?.success) {
      apiError(
        `Fiscal submission failed: ${response?.error ?? 'unknown error'}. The sale is settled — retry fiscal submission from EBM setup.`,
      );
    } else if (response.data?.status === 'FAILED') {
      apiError(
        `Fiscal submission failed: ${response.data.errorMessage ?? 'unknown error'}. The sale is settled — retry fiscal submission from EBM setup.`,
      );
    }
  } catch (err) {
    apiError(
      `Fiscal submission failed: ${err instanceof Error ? err.message : 'unknown error'}. The sale is settled — retry fiscal submission from EBM setup.`,
    );
  }
};

const groupKotItemsByDepartment = (
  items: SalesOrderItem[],
): Map<
  string,
  {
    departmentId: string | null;
    departmentName: string;
    items: SalesOrderItem[];
  }
> =>
  items.reduce<
    Map<
      string,
      {
        departmentId: string | null;
        departmentName: string;
        items: SalesOrderItem[];
      }
    >
  >((acc, item) => {
    const departmentId = item.department_id || null;
    const departmentName =
      (item.department_name || 'GENERAL').trim() || 'GENERAL';
    const key = departmentId || `name:${departmentName.toLowerCase()}`;
    const existing = acc.get(key);

    if (existing) {
      existing.items.push(item);
      return acc;
    }

    acc.set(key, {
      departmentId,
      departmentName,
      items: [item],
    });
    return acc;
  }, new Map());

const resolveKotPrinterByDepartment = (
  state: RunningOrderThunkState,
  sectionId: number,
  departmentId: string | null,
  departmentName: string,
):
  | {
      success: true;
      printer: OutletPrinter;
    }
  | {
      success: false;
      error: string;
    } => {
  const mapping = state.printers.sectionMappings.find((entry) => {
    if (entry.section_id !== sectionId) return false;
    if (entry.operation !== 'KOT') return false;
    if (!entry.printer_id) return false;

    if (departmentId) {
      return entry.department_id === departmentId;
    }

    return (
      !entry.department_id &&
      (entry.department_name || '').trim().toLowerCase() ===
        departmentName.trim().toLowerCase()
    );
  });

  if (!mapping) {
    return {
      success: false,
      error: `No KOT printer is configured for department ${departmentName}`,
    };
  }

  const printer = state.printers.outletPrinters.find(
    (entry) => entry.id === mapping.printer_id && entry.is_active,
  );

  if (!printer) {
    return {
      success: false,
      error: `Configured KOT printer for ${departmentName} is missing or inactive`,
    };
  }

  return {
    success: true,
    printer,
  };
};

const matchesKotDepartment = (
  item: SalesOrderItem,
  departmentId: string | null,
  departmentName: string,
): boolean => {
  if (departmentId) {
    return item.department_id === departmentId;
  }

  const itemDepartmentName =
    (item.department_name || 'GENERAL').trim() || 'GENERAL';
  return !item.department_id && itemDepartmentName === departmentName;
};

const resolveTakeAwayKotPrinterByDepartment = (
  state: RunningOrderThunkState,
  departmentId: string | null,
  departmentName: string,
):
  | {
      success: true;
      printer: OutletPrinter;
    }
  | {
      success: false;
      error: string;
    } => {
  const { deviceId, takeAwayMappings } = state.printers;
  if (!deviceId) {
    return {
      success: false,
      error: 'Current device ID is not available for takeaway KOT printing',
    };
  }

  const mapping = takeAwayMappings.find((entry) => {
    if (entry.device_id !== deviceId) return false;
    if (entry.operation !== 'KOT') return false;
    if (!entry.printer_id) return false;

    if (departmentId) {
      return entry.department_id === departmentId;
    }

    return (
      !entry.department_id &&
      (entry.department_name || '').trim().toLowerCase() ===
        departmentName.trim().toLowerCase()
    );
  });

  if (!mapping) {
    return {
      success: false,
      error: `No takeaway KOT printer is configured for department ${departmentName} on this device`,
    };
  }

  const printer = state.printers.outletPrinters.find(
    (entry) => entry.id === mapping.printer_id && entry.is_active,
  );

  if (!printer) {
    return {
      success: false,
      error: `Configured takeaway KOT printer for ${departmentName} is missing or inactive`,
    };
  }

  return {
    success: true,
    printer,
  };
};

const buildKotPrintPayload = (
  state: RunningOrderThunkState,
  order: SalesOrder,
  items: SalesOrderItem[],
  departmentName: string,
  kotId: number,
) => {
  const authOutlet = state.auth.user?.outlet ?? null;
  return {
    outlet_name: order.outlet_name,
    outlet_address: authOutlet?.address ?? null,
    outlet_tin: authOutlet?.tin_number ?? null,
    outlet_contact: authOutlet?.phone ?? null,
    bill_number: order.bill_number,
    table_name:
      order.table_name || (isTakeAwayLikeOrder(order) ? 'ORDER' : '-'),
    section_name:
      order.section_name || (isTakeAwayLikeOrder(order) ? 'TAKEAWAY' : '-'),
    waiter_name: order.waiter_name || '-',
    printed_by: getPrintedByName(state),
    printed_at: new Date().toISOString(),
    kot_id: kotId,
    items: items.map((item) => ({
      name: item.name,
      quantity: item.quantity,
      notes: item.notes,
      amount: item.amount,
      department_name: departmentName,
    })),
  };
};

const printKotItemsForDepartment = async (
  state: RunningOrderThunkState,
  order: SalesOrder,
  departmentId: string | null,
  departmentName: string,
  kotId: number,
  items: SalesOrderItem[],
) => {
  const printerResult = isTakeAwayLikeOrder(order)
    ? resolveTakeAwayKotPrinterByDepartment(state, departmentId, departmentName)
    : resolveKotPrinterByDepartment(
        state,
        order.section_id,
        departmentId,
        departmentName,
      );

  if (!printerResult.success) {
    return {
      success: false as const,
      error: printerResult.error,
    };
  }

  const printResponse = await printKotTicket({
    printer: {
      name: printerResult.printer.name,
      connection_type: printerResult.printer.connection_type,
      printer_identifier: printerResult.printer.printer_identifier,
      ip_address: printerResult.printer.ip_address,
      port: printerResult.printer.port,
      paper_size: printerResult.printer.paper_size,
      details: printerResult.printer.details,
    },
    payload: buildKotPrintPayload(state, order, items, departmentName, kotId),
  });

  if (!printResponse.success) {
    const base =
      printResponse.error || `Failed to print KOT for ${departmentName}`;
    return {
      success: false as const,
      // Prefix with the configured printer name so operators know which device failed.
      error: `[${printerResult.printer.name}] ${base}`,
    };
  }

  return {
    success: true as const,
    printerName: printerResult.printer.name,
  };
};

const getBillClientDetails = async (
  order: SalesOrder,
): Promise<BillPrintTicketClient | null> => {
  if (!order.client_id && !order.client_name) return null;

  const clientsResponse = await localFetchClients();
  if (!clientsResponse.success || !clientsResponse.data) {
    return order.client_name ? { name: order.client_name } : null;
  }

  const matched = clientsResponse.data.find(
    (client) => client.id === order.client_id,
  );
  if (!matched) {
    return order.client_name ? { name: order.client_name } : null;
  }

  return {
    name: matched.full_name || order.client_name || null,
    address: matched.address || null,
    tin_number: matched.tin_number || null,
    email: matched.email || null,
    contact: matched.contact || null,
  };
};

/** Fetch order items from local SQLite. */
const getSafeOrderItems = async (
  orderId: string,
): Promise<SalesOrderItem[]> => {
  const itemsResponse = await localFetchOrderItems(orderId);
  if (!itemsResponse.success) {
    apiError(itemsResponse.error || 'Failed to fetch order items');
    return [];
  }
  return itemsResponse.data || [];
};

/** Fetch open takeaway/cash orders from local SQLite. */
const getSafeTakeAwayOrders = async (
  outletId: string,
  ctx: LocalOrderContext,
): Promise<SalesOrder[]> => {
  const response = await localFetchOpenTakeAwayOrders(outletId, ctx);
  if (!response.success) {
    apiError(response.error || 'Failed to fetch takeaway orders');
    return [];
  }
  return response.data || [];
};

// ── Name-fallback helpers ─────────────────────────────────────────────────────

/**
 * Enrich order items whose `name` is the local JOIN-miss placeholder using a
 * pre-built lookup map.
 *
 * When PowerSync's menu_items table has not yet synced to local SQLite (or
 * the row was pruned for another reason), the LEFT JOIN in
 * localFetchOrderItems returns NULL for item_name, which mapLocalOrderItem
 * turns into UNAVAILABLE_ITEM_NAME rather than an empty string. This helper
 * fills in real names from Redux (items already displayed in the order
 * panel) or from the MenuItem that was just submitted, ensuring the UI
 * never shows the placeholder during the sync warm-up window.
 *
 * Items whose name is already populated by the SQLite JOIN are left untouched.
 */
function enrichItemNames(
  items: SalesOrderItem[],
  knownNames: ReadonlyMap<string, string>,
): SalesOrderItem[] {
  if (knownNames.size === 0) return items;
  return items.map((item) => {
    if (item.name !== UNAVAILABLE_ITEM_NAME || !item.menu_item_id) {
      return item;
    }
    const fallback = knownNames.get(item.menu_item_id);
    return fallback ? { ...item, name: fallback } : item;
  });
}

/**
 * Build a menu_item_id → name lookup map from items already in Redux.
 * Pass `extra` (the MenuItem just submitted) to include names for brand-new
 * items that do not yet appear in selectedOrderItems. Items whose own name
 * is still the JOIN-miss placeholder are excluded — they don't have a real
 * known name to contribute to the map.
 */
function buildNameMap(
  existingItems: readonly SalesOrderItem[],
  extra?: MenuItem | null,
): Map<string, string> {
  const map = new Map<string, string>();
  existingItems.forEach((item) => {
    if (item.menu_item_id && item.name && item.name !== UNAVAILABLE_ITEM_NAME) {
      map.set(item.menu_item_id, item.name);
    }
  });
  if (extra?.id && extra?.name) {
    map.set(extra.id, extra.name);
  }
  return map;
}

// ─────────────────────────────────────────────────────────────────────────────

/** Re-fetch order + items from local SQLite after a write. */
const refreshOrderContext = async (
  orderId: string,
  ctx: LocalOrderContext,
  nameHints?: ReadonlyMap<string, string>,
): Promise<
  | { success: true; order: SalesOrder; items: SalesOrderItem[] }
  | { success: false; error: string }
> => {
  const [orderResponse, itemsResponse] = await Promise.all([
    localFetchOrderById(orderId, ctx),
    localFetchOrderItems(orderId),
  ]);

  if (!orderResponse.success || !orderResponse.data) {
    return {
      success: false,
      error: orderResponse.error || 'Failed to refresh order details',
    };
  }

  if (!itemsResponse.success) {
    return {
      success: false,
      error: itemsResponse.error || 'Failed to refresh order items',
    };
  }

  const rawItems = itemsResponse.data || [];
  const items = nameHints ? enrichItemNames(rawItems, nameHints) : rawItems;

  return {
    success: true,
    order: orderResponse.data,
    items,
  };
};

/** Fetch a fresh order and guard against pending items before a gated action. */
const getFreshOrderForAction = async (
  orderId: string,
  actionLabel: string,
  ctx: LocalOrderContext,
): Promise<
  { success: true; order: SalesOrder } | { success: false; error: string }
> => {
  const orderResponse = await localFetchOrderById(orderId, ctx);
  if (!orderResponse.success || !orderResponse.data) {
    return {
      success: false,
      error: orderResponse.error || 'Failed to fetch order details',
    };
  }

  if (orderResponse.data.has_pending_items) {
    return {
      success: false,
      error: `This order has unconfirmed items. Confirm the order (KOT) before ${actionLabel}.`,
    };
  }

  return { success: true, order: orderResponse.data };
};

export const selectDiningTableOrder = createAsyncThunk<
  RunningOrderSelectionResult,
  SelectedTable,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/selectDiningTableOrder',
  async (table, { getState, rejectWithValue }) => {
    const state = getState();
    const ctx = buildLocalCtx(state);
    const runningOrderResponse = await localFetchRunningTableOrder(
      table.id,
      ctx,
    );
    if (!runningOrderResponse.success) {
      const message =
        runningOrderResponse.error || 'Failed to fetch table order';
      apiError(message);
      return rejectWithValue(message);
    }

    if (!runningOrderResponse.data) {
      if (!hasAccessControl(state, AccessControlCode.SALES_ORDERS_CREATE)) {
        const message = 'You do not have permission to create new orders';
        apiInfo(message);
        return rejectWithValue(message);
      }

      return { kind: 'missing', table };
    }

    const rawItems = await getSafeOrderItems(
      runningOrderResponse.data.order_id,
    );
    const nameHints = buildNameMap(state.runningOrder.selectedOrderItems);
    const items = enrichItemNames(rawItems, nameHints);

    return {
      kind: 'found',
      table,
      order: runningOrderResponse.data,
      items,
    };
  },
);

export const fetchTakeAwayOrdersForOutlet = createAsyncThunk<
  SalesOrder[],
  string,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/fetchTakeAwayOrdersForOutlet',
  async (outletId, { getState, rejectWithValue }) => {
    const ctx = buildLocalCtx(getState());
    const response = await localFetchOpenTakeAwayOrders(outletId, ctx);
    if (!response.success) {
      const message = response.error || 'Failed to fetch takeaway orders';
      return rejectWithValue(message);
    }

    return response.data || [];
  },
);

export const selectTakeAwayOrderById = createAsyncThunk<
  { orderId: string; order: SalesOrder; items: SalesOrderItem[] },
  string,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/selectTakeAwayOrderById',
  async (orderId, { getState, rejectWithValue }) => {
    const state = getState();
    const ctx = buildLocalCtx(state);
    const orderResponse = await localFetchOrderById(orderId, ctx);
    if (!orderResponse.success || !orderResponse.data) {
      const message =
        orderResponse.error || 'Failed to fetch takeaway order details';
      apiError(message);
      return rejectWithValue(message);
    }

    const rawItems = await getSafeOrderItems(orderId);
    const nameHints = buildNameMap(state.runningOrder.selectedOrderItems);
    const items = enrichItemNames(rawItems, nameHints);
    return {
      orderId,
      order: orderResponse.data,
      items,
    };
  },
);

export const createTakeAwayOrderForCurrentOutlet = createAsyncThunk<
  RunningOrderTakeAwayOrderResult,
  void,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/createTakeAwayOrderForCurrentOutlet',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const outletId = state.auth.user?.outlet?.id;

    if (!hasAccessControl(state, AccessControlCode.SALES_ORDERS_CREATE)) {
      const message = 'You do not have permission to create new orders';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (!outletId) {
      const message = 'No outlet is assigned to your user profile';
      apiError(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);

    let createResponse;
    if (await isOnline()) {
      createResponse = await createTakeAwayOrder(outletId);
      if (createResponse.success && createResponse.data) {
        await localInsertRpcOrder(createResponse.data);
      }
    } else {
      createResponse = await localCreateTakeawayOrder(ctx);
    }

    if (!createResponse.success || !createResponse.data) {
      const message = createResponse.error || 'Failed to create takeaway order';
      apiError(message);
      return rejectWithValue(message);
    }

    const items = await getSafeOrderItems(createResponse.data.order_id);
    const takeAwayOrders = await getSafeTakeAwayOrders(outletId, ctx);

    apiSuccess(
      `${createResponse.data.mode} order #${createResponse.data.bill_number} created`,
    );

    return {
      order: createResponse.data,
      items,
      takeAwayOrders,
    };
  },
);

export const openSalesReportForCurrentOutlet = createAsyncThunk<
  OutletSalesReport,
  void,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/openSalesReportForCurrentOutlet',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const outlet = state.auth.user?.outlet;

    if (!outlet?.id) {
      const message = 'No outlet is assigned to your user profile';
      apiError(message);
      return rejectWithValue(message);
    }

    const companyId = state.auth.user?.company?.id;
    if (!companyId) {
      const message = 'Unable to resolve company details for this outlet';
      apiError(message);
      return rejectWithValue(message);
    }

    const businessDate = outlet.day_open || getLocalBusinessDateString();
    const response = await fetchOutletSalesReport({
      outletId: outlet.id,
      companyId,
      outletName: outlet.name,
      businessDate,
    });

    if (!response.success || !response.data) {
      const message = response.error || 'Failed to load outlet sales report';
      apiError(message);
      return rejectWithValue(message);
    }

    return response.data;
  },
);

export const exportSalesReportPdfForCurrentOutlet = createAsyncThunk<
  string,
  void,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/exportSalesReportPdfForCurrentOutlet',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const outlet = state.auth.user?.outlet;
    const report = state.runningOrder.salesReport;

    if (!outlet?.id) {
      const message = 'No outlet is assigned to your user profile';
      apiError(message);
      return rejectWithValue(message);
    }

    if (!report) {
      const message = 'Load the sales report before exporting it';
      apiInfo(message);
      return rejectWithValue(message);
    }

    const response = await exportSalesReportPdf({
      report,
      outletName: outlet.name,
      businessDate: report.summary.sale_date,
    });

    if (!response.success || !response.data) {
      const message = response.error || 'Failed to export sales report PDF';
      if (message !== 'Export cancelled') {
        apiError(message);
      }
      return rejectWithValue(message);
    }

    apiSuccess('Sales report PDF exported');
    return response.data.filePath;
  },
);

export const sendSalesReportEmailForCurrentOutlet = createAsyncThunk<
  { recipientCount: number; message: string },
  string | undefined,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/sendSalesReportEmailForCurrentOutlet',
  async (businessDateOverride, { getState, rejectWithValue }) => {
    const state = getState();
    const outlet = state.auth.user?.outlet;
    const report = state.runningOrder.salesReport;

    if (!outlet?.id || !outlet?.company_id) {
      const message = 'No outlet is assigned to your user profile';
      apiError(message);
      return rejectWithValue(message);
    }

    const businessDate = businessDateOverride || report?.summary.sale_date;

    if (!businessDate) {
      const message = 'Business date is required to send the report email';
      apiInfo(message);
      return rejectWithValue(message);
    }

    const response = await sendSalesReportEmail({
      outletId: outlet.id,
      companyId: outlet.company_id,
      outletName: outlet.name || 'Outlet',
      businessDate,
    });

    if (!response.success || !response.data) {
      const message = response.error || 'Failed to send sales report email';
      apiError(message);
      return rejectWithValue(message);
    }

    if (response.data.recipientCount > 0) {
      apiSuccess(
        `Report email queued for ${response.data.recipientCount} recipient(s)`,
      );
    } else {
      apiInfo('No active email configurations found for this report');
    }

    return response.data;
  },
);

export const openNewDayForCurrentOutlet = createAsyncThunk<
  OpenNewDayResult,
  string,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/openNewDayForCurrentOutlet',
  async (targetDate, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const { user } = state.auth;
    const outlet = user?.outlet;

    if (!hasAccessControl(state, AccessControlCode.SALES_DAY_OPEN)) {
      const message = 'You do not have permission to open a new day';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (!outlet?.id) {
      const message = 'No outlet is assigned to your user profile';
      apiError(message);
      return rejectWithValue(message);
    }

    if (!user?.id) {
      const message = 'User details could not be resolved';
      apiError(message);
      return rejectWithValue(message);
    }

    const currentBusinessDay = outlet.day_open || getLocalBusinessDateString();
    if (!targetDate) {
      const message = 'Select a target business day';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (targetDate === currentBusinessDay) {
      const message = 'This is already the current business day';
      apiInfo(message);
      return rejectWithValue(message);
    }

    // Mirrors transition_outlet_business_day's guard: normal forward
    // progress (targetDate > currentBusinessDay) is always allowed, and a
    // target behind currentBusinessDay is allowed too as long as it isn't
    // beyond today — that's how a misclick that over-advanced the business
    // day (e.g. to tomorrow) can be corrected back.
    const todayLocal = getLocalBusinessDateString();
    if (targetDate < currentBusinessDay && targetDate > todayLocal) {
      const message = 'Select a date that is after the current business day';
      apiInfo(message);
      return rejectWithValue(message);
    }

    // Opening a new day is server-authoritative — it locks and inspects
    // every open order at the outlet across every device, not just this
    // one's local view, and can't be safely queued offline. Check first so
    // the user gets a clear, accurate message instead of the Supabase
    // client library's generic "Failed to send a request to the Edge
    // Function" once the network call itself fails.
    if (!(await isOnline())) {
      const message =
        'You are offline — connect to the internet to open a new business day.';
      apiError(message);
      return rejectWithValue(message);
    }

    const updateResponse = await openNewDayService({
      userId: user.id,
      outletId: outlet.id,
      targetDate,
    });

    if (!updateResponse.success || !updateResponse.data) {
      const message = updateResponse.error || 'Failed to open a new day';
      apiError(message);
      return rejectWithValue(message);
    }

    await dispatch(fetchCurrentUser());

    if (outlet.id) {
      await dispatch(fetchTables(outlet.id));
      await dispatch(fetchTakeAwayOrdersForOutlet(outlet.id));
    }

    const emptyOrdersMessage =
      updateResponse.data.autoCancelledEmptyOrderCount > 0
        ? ` ${updateResponse.data.autoCancelledEmptyOrderCount} empty order(s) were auto-cancelled.`
        : '';

    const emailQueuedMessage = updateResponse.data.reportEmailQueued
      ? ` Sales report email queued for ${updateResponse.data.superUserEmailCount} super user(s).`
      : '';

    const successMessage = `New day opened for ${formatBusinessDateForMessage(targetDate)}.${emptyOrdersMessage}${emailQueuedMessage}`;

    apiSuccess(successMessage);
    return updateResponse.data;
  },
);

export const confirmCreateOrderForSelectedTable = createAsyncThunk<
  RunningOrderCreateResult,
  void,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/confirmCreateOrderForSelectedTable',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const { createOrderTargetTable: targetTable } = state.runningOrder;

    if (!hasAccessControl(state, AccessControlCode.SALES_ORDERS_CREATE)) {
      const message = 'You do not have permission to create new orders';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (!targetTable) {
      const message = 'Select a table to create an order';
      apiInfo(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);

    // Online: use the Supabase RPC — atomically assigns bill_number with a
    // Postgres-level lock, so two devices can never get the same value.
    // Offline: fall back to the local counter path (writeTransaction guards
    // same-device races; cross-device conflicts are handled in the connector).
    let createOrderResponse;
    if (await isOnline()) {
      createOrderResponse = await getOrCreateTableOrder(targetTable.id);
      if (createOrderResponse.success && createOrderResponse.data) {
        await localInsertRpcOrder(createOrderResponse.data);
      }
    } else {
      createOrderResponse = await localGetOrCreateTableOrder(
        targetTable.id,
        ctx,
      );
    }

    if (!createOrderResponse.success || !createOrderResponse.data) {
      const message = createOrderResponse.error || 'Failed to create order';
      apiError(message);
      return rejectWithValue(message);
    }

    const items = await getSafeOrderItems(createOrderResponse.data.order_id);
    const outletId = state.auth.user?.outlet?.id;
    if (outletId) {
      dispatch(fetchTables(outletId));
      dispatch(fetchTakeAwayOrdersForOutlet(outletId));
    }

    apiSuccess(`Order created for ${targetTable.name}`);

    return {
      table: targetTable,
      order: createOrderResponse.data,
      items,
    };
  },
);

export const submitSelectedOrderItem = createAsyncThunk<
  { order: SalesOrder; items: SalesOrderItem[] },
  AddOrderItemPayload,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/submitSelectedOrderItem',
  async ({ notes, quantity }, { getState, rejectWithValue }) => {
    const state = getState();
    const { menuItemForModal: menuItem, selectedOrder } = state.runningOrder;

    if (!selectedOrder || !menuItem) {
      const message = 'No selected order or menu item';
      apiError(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const addItemResponse = await localAddOrderItem(
      selectedOrder.order_id,
      menuItem.id,
      quantity,
      menuItem.base_price,
      notes,
      ctx,
      menuItem.name, // fallback name while menu_items syncs to local SQLite
    );

    if (!addItemResponse.success) {
      const message = addItemResponse.error || 'Failed to add item to order';
      apiError(message);
      return rejectWithValue(message);
    }

    // Pass menuItem as `extra` so the just-added item gets its name even when
    // the menu_items table has not yet appeared in local SQLite.
    const refreshedContext = await refreshOrderContext(
      selectedOrder.order_id,
      ctx,
      buildNameMap(state.runningOrder.selectedOrderItems, menuItem),
    );
    if (!refreshedContext.success) {
      apiError(refreshedContext.error);
      return rejectWithValue(refreshedContext.error);
    }

    apiSuccess(`${menuItem.name} added to order`);

    return {
      order: refreshedContext.order,
      items: refreshedContext.items,
    };
  },
);

export const openDiscountForSelectedOrder = createAsyncThunk<
  SalesOrder,
  void,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/openDiscountForSelectedOrder',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!hasAccessControl(state, AccessControlCode.SALES_DISCOUNTS_OFFER)) {
      const message = 'You do not have permission to offer discounts';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (!selectedOrder) {
      const message = 'Select or create an order first';
      apiInfo(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const freshOrderResult = await getFreshOrderForAction(
      selectedOrder.order_id,
      'adding a discount',
      ctx,
    );

    if (!freshOrderResult.success) {
      apiError(freshOrderResult.error);
      return rejectWithValue(freshOrderResult.error);
    }

    return freshOrderResult.order;
  },
);

export const submitSelectedOrderDiscount = createAsyncThunk<
  { order: SalesOrder; items: SalesOrderItem[] },
  AddOrderDiscountPayload,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/submitSelectedOrderDiscount',
  async ({ notes, type, value }, { getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!hasAccessControl(state, AccessControlCode.SALES_DISCOUNTS_OFFER)) {
      const message = 'You do not have permission to offer discounts';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (!selectedOrder) {
      const message = 'No selected order';
      apiError(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const freshOrderResult = await getFreshOrderForAction(
      selectedOrder.order_id,
      'adding a discount',
      ctx,
    );

    if (!freshOrderResult.success) {
      apiError(freshOrderResult.error);
      return rejectWithValue(freshOrderResult.error);
    }

    const response = await localAddOrderDiscount(
      selectedOrder.order_id,
      value,
      type,
      notes,
      ctx,
    );
    if (!response.success) {
      const message = response.error || 'Failed to apply discount';
      apiError(message);
      return rejectWithValue(message);
    }

    const refreshedContext = await refreshOrderContext(
      selectedOrder.order_id,
      ctx,
      buildNameMap(state.runningOrder.selectedOrderItems),
    );
    if (!refreshedContext.success) {
      apiError(refreshedContext.error);
      return rejectWithValue(refreshedContext.error);
    }

    apiSuccess('Discount applied');

    return {
      order: refreshedContext.order,
      items: refreshedContext.items,
    };
  },
);

export const removeSelectedOrderDiscount = createAsyncThunk<
  { order: SalesOrder; items: SalesOrderItem[] },
  void,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/removeSelectedOrderDiscount',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!hasAccessControl(state, AccessControlCode.SALES_DISCOUNTS_OFFER)) {
      const message = 'You do not have permission to offer discounts';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (!selectedOrder) {
      const message = 'No selected order';
      apiError(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const freshOrderResult = await getFreshOrderForAction(
      selectedOrder.order_id,
      'updating discount',
      ctx,
    );

    if (!freshOrderResult.success) {
      apiError(freshOrderResult.error);
      return rejectWithValue(freshOrderResult.error);
    }

    const removeResponse = await localRemoveOrderDiscount(
      selectedOrder.order_id,
    );
    if (!removeResponse.success) {
      const message = removeResponse.error || 'Failed to remove discount';
      apiError(message);
      return rejectWithValue(message);
    }

    if (!removeResponse.data) {
      const message = 'This order has no existing discount';
      apiInfo(message);
      return rejectWithValue(message);
    }

    const refreshedContext = await refreshOrderContext(
      selectedOrder.order_id,
      ctx,
      buildNameMap(state.runningOrder.selectedOrderItems),
    );
    if (!refreshedContext.success) {
      apiError(refreshedContext.error);
      return rejectWithValue(refreshedContext.error);
    }

    apiSuccess('Discount removed');

    return {
      order: refreshedContext.order,
      items: refreshedContext.items,
    };
  },
);

export const openWaiterModalForSelectedOrder = createAsyncThunk<
  RunningOrderWaiterModalOpenResult,
  void,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/openWaiterModalForSelectedOrder',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;
    const authOutletId = state.auth.user?.outlet?.id || null;

    if (!selectedOrder) {
      const message = 'Select or create an order first';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (!authOutletId) {
      const message = 'No outlet is assigned to your user profile';
      apiError(message);
      return rejectWithValue(message);
    }

    if (selectedOrder.outlet_id !== authOutletId) {
      const message = 'You can only manage waiters for your current outlet';
      apiError(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const freshOrderResponse = await localFetchOrderById(
      selectedOrder.order_id,
      ctx,
    );
    if (!freshOrderResponse.success || !freshOrderResponse.data) {
      const message =
        freshOrderResponse.error || 'Failed to fetch latest order details';
      apiError(message);
      return rejectWithValue(message);
    }

    const waitersResponse = await localFetchWaiters(
      freshOrderResponse.data.outlet_id,
    );
    if (!waitersResponse.success) {
      const message = waitersResponse.error || 'Failed to fetch outlet waiters';
      apiError(message);
      return rejectWithValue(message);
    }

    return {
      order: freshOrderResponse.data,
      waiters: waitersResponse.data || [],
    };
  },
);

export const submitSelectedOrderWaiter = createAsyncThunk<
  RunningOrderWaiterAssignResult,
  UpdateOrderWaiterPayload,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/submitSelectedOrderWaiter',
  async ({ waiterId }, { getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder, outletWaiters } = state.runningOrder;
    const authOutletId = state.auth.user?.outlet?.id || null;

    if (!selectedOrder) {
      const message = 'No selected order';
      apiError(message);
      return rejectWithValue(message);
    }

    if (!waiterId) {
      const message = 'Select a waiter first';
      apiError(message);
      return rejectWithValue(message);
    }

    if (!authOutletId) {
      const message = 'No outlet is assigned to your user profile';
      apiError(message);
      return rejectWithValue(message);
    }

    if (selectedOrder.outlet_id !== authOutletId) {
      const message = 'You can only manage waiters for your current outlet';
      apiError(message);
      return rejectWithValue(message);
    }

    const selectedWaiter = outletWaiters.find(
      (waiter) => waiter.id === waiterId,
    );
    if (!selectedWaiter) {
      const message = 'Selected waiter was not found in this outlet';
      apiError(message);
      return rejectWithValue(message);
    }

    if (selectedWaiter.outlet_id !== selectedOrder.outlet_id) {
      const message = 'Waiter must belong to the same outlet as the order';
      apiError(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const updateResponse = await localUpdateOrderWaiter(
      selectedOrder.order_id,
      waiterId,
    );
    if (!updateResponse.success || !updateResponse.data) {
      const message = updateResponse.error || 'Failed to update order waiter';
      apiError(message);
      return rejectWithValue(message);
    }

    const refreshedContext = await refreshOrderContext(
      selectedOrder.order_id,
      ctx,
      buildNameMap(state.runningOrder.selectedOrderItems),
    );
    if (!refreshedContext.success) {
      apiError(refreshedContext.error);
      return rejectWithValue(refreshedContext.error);
    }

    apiSuccess(
      `Waiter updated to ${selectedWaiter.full_name || selectedWaiter.first_name || 'selected waiter'}`,
    );

    return {
      order: refreshedContext.order,
      items: refreshedContext.items,
    };
  },
);

export const openClientModalForSelectedOrder = createAsyncThunk<
  RunningOrderClientModalOpenResult,
  void,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/openClientModalForSelectedOrder',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!selectedOrder) {
      const message = 'Select or create an order first';
      apiInfo(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const freshOrderResponse = await localFetchOrderById(
      selectedOrder.order_id,
      ctx,
    );
    if (!freshOrderResponse.success || !freshOrderResponse.data) {
      const message =
        freshOrderResponse.error || 'Failed to fetch latest order details';
      apiError(message);
      return rejectWithValue(message);
    }

    const clientsResponse = await localFetchClients();
    if (!clientsResponse.success) {
      const message = clientsResponse.error || 'Failed to fetch clients';
      apiError(message);
      return rejectWithValue(message);
    }

    return {
      order: freshOrderResponse.data,
      clients: clientsResponse.data || [],
    };
  },
);

export const assignClientForSelectedOrder = createAsyncThunk<
  RunningOrderClientAssignResult,
  AssignClientPayload,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/assignClientForSelectedOrder',
  async ({ clientId }, { getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!selectedOrder) {
      const message = 'No selected order';
      apiError(message);
      return rejectWithValue(message);
    }

    if (!clientId) {
      const message = 'Select a client first';
      apiError(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const assignResponse = await localAssignClientToOrder(
      selectedOrder.order_id,
      clientId,
    );
    if (!assignResponse.success || !assignResponse.data) {
      const message = assignResponse.error || 'Failed to add client to order';
      apiError(message);
      return rejectWithValue(message);
    }

    const refreshedContext = await refreshOrderContext(
      selectedOrder.order_id,
      ctx,
      buildNameMap(state.runningOrder.selectedOrderItems),
    );
    if (!refreshedContext.success) {
      apiError(refreshedContext.error);
      return rejectWithValue(refreshedContext.error);
    }

    const clientsResponse = await localFetchClients();
    if (!clientsResponse.success) {
      const message = clientsResponse.error || 'Failed to refresh clients';
      apiError(message);
      return rejectWithValue(message);
    }

    apiSuccess('Client added to order');

    return {
      order: refreshedContext.order,
      items: refreshedContext.items,
      clients: clientsResponse.data || [],
    };
  },
);

export const createClientForSelectedOrder = createAsyncThunk<
  RunningOrderClientAssignResult,
  CreateClientForSelectedOrderPayload,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/createClientForSelectedOrder',
  async (
    { address, contact, email, firstName, lastName, tinNumber },
    { getState, rejectWithValue },
  ) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!selectedOrder) {
      const message = 'No selected order';
      apiError(message);
      return rejectWithValue(message);
    }

    if (!firstName.trim() || !lastName.trim()) {
      const message = 'First name and last name are required';
      apiError(message);
      return rejectWithValue(message);
    }

    const createClientResponse = await createCompanyClient({
      company_id: selectedOrder.company_id,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email,
      contact,
      address,
      tin_number: tinNumber,
    });

    if (!createClientResponse.success || !createClientResponse.data) {
      const message = createClientResponse.error || 'Failed to create client';
      apiError(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const assignResponse = await localAssignClientToOrder(
      selectedOrder.order_id,
      createClientResponse.data.id,
    );

    if (!assignResponse.success || !assignResponse.data) {
      const message = assignResponse.error || 'Failed to add client to order';
      apiError(message);
      return rejectWithValue(message);
    }

    const refreshedContext = await refreshOrderContext(
      selectedOrder.order_id,
      ctx,
      buildNameMap(state.runningOrder.selectedOrderItems),
    );
    if (!refreshedContext.success) {
      apiError(refreshedContext.error);
      return rejectWithValue(refreshedContext.error);
    }

    const clientsResponse = await localFetchClients();
    if (!clientsResponse.success) {
      const message = clientsResponse.error || 'Failed to refresh clients';
      apiError(message);
      return rejectWithValue(message);
    }

    apiSuccess('Client created and added to order');

    return {
      order: refreshedContext.order,
      items: refreshedContext.items,
      clients: clientsResponse.data || [],
    };
  },
);

export const openBillForSelectedOrder = createAsyncThunk<
  SalesOrder,
  void,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/openBillForSelectedOrder',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!hasAccessControl(state, AccessControlCode.SALES_BILLS_PRINT)) {
      const message = 'You do not have permission to print bills';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (!selectedOrder) {
      const message = 'Select or create an order first';
      apiInfo(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const freshOrderResult = await getFreshOrderForAction(
      selectedOrder.order_id,
      'printing a bill',
      ctx,
    );

    if (!freshOrderResult.success) {
      apiError(freshOrderResult.error);
      return rejectWithValue(freshOrderResult.error);
    }

    const refreshedContext = await refreshOrderContext(
      selectedOrder.order_id,
      ctx,
      buildNameMap(state.runningOrder.selectedOrderItems),
    );
    if (!refreshedContext.success) {
      apiError(refreshedContext.error);
      return rejectWithValue(refreshedContext.error);
    }

    const authOutlet = state.auth.user?.outlet ?? null;
    const useTakeAwayPrinter = isTakeAwayLikeOrder(freshOrderResult.order);

    const printerResult = useTakeAwayPrinter
      ? resolveTakeAwayPrinter(state, 'BILL')
      : resolveSectionPrinter(state, freshOrderResult.order.section_id, 'BILL');
    if (!printerResult.success) {
      apiError(printerResult.error);
      return rejectWithValue(printerResult.error);
    }

    const billItems = getBillLineItems(refreshedContext.items);
    if (billItems.length === 0) {
      const message = 'This order has no billable items';
      apiInfo(message);
      return rejectWithValue(message);
    }
    const clientDetails = await getBillClientDetails(refreshedContext.order);

    const printResponse = await printBillTicket({
      printer: {
        name: printerResult.printer.name,
        connection_type: printerResult.printer.connection_type,
        printer_identifier: printerResult.printer.printer_identifier,
        ip_address: printerResult.printer.ip_address,
        port: printerResult.printer.port,
        paper_size: printerResult.printer.paper_size,
        details: printerResult.printer.details,
      },
      payload: {
        outlet_name: refreshedContext.order.outlet_name,
        outlet_address: authOutlet?.address ?? null,
        outlet_tin: authOutlet?.tin_number ?? null,
        outlet_contact: authOutlet?.phone ?? null,
        bill_number: refreshedContext.order.bill_number,
        table_name:
          refreshedContext.order.table_name ||
          (useTakeAwayPrinter ? 'ORDER' : '-'),
        section_name:
          refreshedContext.order.section_name ||
          (useTakeAwayPrinter ? 'TAKEAWAY' : '-'),
        waiter_name: refreshedContext.order.waiter_name || '-',
        printed_by: getPrintedByName(state),
        printed_at: new Date().toISOString(),
        items: billItems,
        client: clientDetails,
        total_amount: refreshedContext.order.total_amount,
        discount_amount: refreshedContext.order.discount_amount,
        net_amount: refreshedContext.order.net_amount,
        outstanding_balance:
          typeof refreshedContext.order.balance_amount === 'number'
            ? refreshedContext.order.balance_amount
            : null,
        balance_brought_forward:
          typeof refreshedContext.order.settlement_total === 'number'
            ? refreshedContext.order.net_amount -
              refreshedContext.order.settlement_total
            : null,
        footer_lines: [
          ...(authOutlet?.receipt_footnote
            ? [authOutlet.receipt_footnote]
            : []),
          'Powered by smartPOS Cloud',
          'www.smartposcloud.app',
        ],
      },
    });

    if (!printResponse.success) {
      const message = printResponse.error || 'Failed to print bill';
      apiError(message);
      return rejectWithValue(message);
    }

    await localSetOrderBillPrinted(refreshedContext.order.order_id, true);

    apiSuccess(
      `Bill #${refreshedContext.order.bill_number} sent to ${printerResult.printer.name}`,
    );

    return { ...refreshedContext.order, is_bill_printed: true };
  },
);

export const confirmKotForSelectedOrder = createAsyncThunk<
  RunningOrderKotConfirmResult,
  string | undefined,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/confirmKotForSelectedOrder',
  async (physicalKotNumber, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!selectedOrder) {
      const message = 'Select or create an order first';
      apiInfo(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const freshOrderResponse = await localFetchOrderById(
      selectedOrder.order_id,
      ctx,
    );
    if (!freshOrderResponse.success || !freshOrderResponse.data) {
      const message =
        freshOrderResponse.error || 'Failed to fetch latest order details';
      apiError(message);
      return rejectWithValue(message);
    }

    if (!freshOrderResponse.data.has_pending_items) {
      const message = 'This order has no pending items to confirm';
      apiInfo(message);
      return rejectWithValue(message);
    }

    const kotResponse = await localConfirmOrderKot(
      selectedOrder.order_id,
      ctx,
      physicalKotNumber,
    );
    const kotPayload = kotResponse.data;
    if (!kotResponse.success || !kotPayload) {
      const message = kotResponse.error || 'Failed to confirm order items';
      apiError(message);
      return rejectWithValue(message);
    }

    const refreshedContext = await refreshOrderContext(
      selectedOrder.order_id,
      ctx,
      buildNameMap(state.runningOrder.selectedOrderItems),
    );
    if (!refreshedContext.success) {
      apiError(refreshedContext.error);
      return rejectWithValue(refreshedContext.error);
    }

    const outletId = state.auth.user?.outlet?.id;
    if (outletId) {
      dispatch(fetchTables(outletId));
      dispatch(fetchTakeAwayOrdersForOutlet(outletId));
    }

    const confirmedKotItems = refreshedContext.items.filter(
      (item) =>
        item.kot_id === kotPayload.kot_id && item.status === 'CONFIRMED',
    );

    if (confirmedKotItems.length > 0) {
      const groupedKotItems = groupKotItemsByDepartment(confirmedKotItems);
      const printableGroups = (
        await Promise.all(
          Array.from(groupedKotItems.values()).map(async (group) => {
            // Groups with no departmentId (the 'GENERAL' fallback bucket) have
            // no department row to disable, so they always remain printable.
            const kotEnabled = group.departmentId
              ? await localGetDepartmentKotEnabled(group.departmentId)
              : true;
            return { ...group, kotEnabled };
          }),
        )
      ).filter((group) => group.kotEnabled);

      const printResults = await Promise.all(
        printableGroups.map(
          async ({ departmentId, departmentName, items: departmentItems }) => {
            const printResponse = await printKotItemsForDepartment(
              getState(),
              refreshedContext.order,
              departmentId,
              departmentName,
              kotPayload.kot_id,
              departmentItems,
            );

            return {
              departmentName,
              printResponse,
            };
          },
        ),
      );

      const printErrors = printResults
        .filter(({ printResponse }) => !printResponse.success)
        .map(
          ({ departmentName, printResponse }) =>
            `${departmentName}: ${printResponse.error || 'Unknown print error'}`,
        );

      if (printErrors.length > 0) {
        apiInfo(
          `KOT confirmed but some department prints failed: ${printErrors.join(' | ')}`,
        );
      }
    }

    apiSuccess(
      `KOT #${kotPayload.kot_id} confirmed (${kotPayload.confirmed_count} item entr${
        kotPayload.confirmed_count === 1 ? 'y' : 'ies'
      })`,
    );

    return {
      kot_id: kotPayload.kot_id,
      confirmed_count: kotPayload.confirmed_count,
      order: refreshedContext.order,
      items: refreshedContext.items,
    };
  },
);

export const reprintKotForSelectedOrderItem = createAsyncThunk<
  { kotId: number; printerName: string },
  ReprintKotPayload,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/reprintKotForSelectedOrderItem',
  async ({ orderItemId }, { getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!hasAccessControl(state, AccessControlCode.SALES_KOTS_REPRINT)) {
      const message = 'You do not have permission to re-print KOTs';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (!selectedOrder) {
      const message = 'Select or create an order first';
      apiInfo(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const refreshedContext = await refreshOrderContext(
      selectedOrder.order_id,
      ctx,
      buildNameMap(state.runningOrder.selectedOrderItems),
    );
    if (!refreshedContext.success) {
      apiError(refreshedContext.error);
      return rejectWithValue(refreshedContext.error);
    }

    const targetItem = refreshedContext.items.find(
      (item) => item.id === orderItemId,
    );

    if (!targetItem) {
      const message = 'The selected order item could not be found';
      apiError(message);
      return rejectWithValue(message);
    }

    if (targetItem.status !== 'CONFIRMED' || !targetItem.kot_id) {
      const message = 'Only confirmed KOT items can be re-printed';
      apiInfo(message);
      return rejectWithValue(message);
    }

    const departmentId = targetItem.department_id || null;
    const departmentName =
      (targetItem.department_name || 'GENERAL').trim() || 'GENERAL';

    const kotItems = refreshedContext.items.filter(
      (item) =>
        item.status === 'CONFIRMED' &&
        item.kot_id === targetItem.kot_id &&
        matchesKotDepartment(item, departmentId, departmentName),
    );

    if (kotItems.length === 0) {
      const message = 'This KOT no longer has printable items';
      apiInfo(message);
      return rejectWithValue(message);
    }

    const printResponse = await printKotItemsForDepartment(
      state,
      refreshedContext.order,
      departmentId,
      departmentName,
      targetItem.kot_id,
      kotItems,
    );

    if (!printResponse.success) {
      apiError(printResponse.error);
      return rejectWithValue(printResponse.error);
    }

    apiSuccess(
      `KOT #${targetItem.kot_id} re-printed to ${printResponse.printerName}`,
    );

    return {
      kotId: targetItem.kot_id,
      printerName: printResponse.printerName,
    };
  },
);

export const openSettleModalForSelectedOrder = createAsyncThunk<
  RunningOrderSettleOpenResult,
  void,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/openSettleModalForSelectedOrder',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!selectedOrder) {
      const message = 'Select or create an order first';
      apiInfo(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const freshOrderResult = await getFreshOrderForAction(
      selectedOrder.order_id,
      'settling the order',
      ctx,
    );

    if (!freshOrderResult.success) {
      apiError(freshOrderResult.error);
      return rejectWithValue(freshOrderResult.error);
    }

    const paymentMethodsResponse = await localFetchPaymentMethods();
    const clientsResponse = await localFetchClients();

    if (!paymentMethodsResponse.success) {
      const message =
        paymentMethodsResponse.error || 'Failed to fetch payment methods';
      apiError(message);
      return rejectWithValue(message);
    }

    if ((paymentMethodsResponse.data || []).length === 0) {
      const message = 'No supported payment methods are configured';
      apiError(message);
      return rejectWithValue(message);
    }

    if (!clientsResponse.success) {
      apiInfo('Clients could not be loaded for credit settlements');
    }

    return {
      order: freshOrderResult.order,
      paymentMethods: paymentMethodsResponse.data || [],
      clients: clientsResponse.data || [],
    };
  },
);

export const submitDirectSettlementForSelectedOrder = createAsyncThunk<
  SettlementReceiptContext,
  DirectSettlementPayload,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/submitDirectSettlementForSelectedOrder',
  async (
    { notes, paymentMethodId },
    { dispatch, getState, rejectWithValue },
  ) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!selectedOrder) {
      const message = 'No selected order';
      apiError(message);
      return rejectWithValue(message);
    }

    const fiscalBlockMessage = await assertFiscalReadiness(
      state,
      selectedOrder.order_id,
    );
    if (fiscalBlockMessage) {
      apiError(fiscalBlockMessage);
      return rejectWithValue(fiscalBlockMessage);
    }

    const ctx = buildLocalCtx(state);
    const freshOrderResult = await getFreshOrderForAction(
      selectedOrder.order_id,
      'settling the order',
      ctx,
    );

    if (!freshOrderResult.success) {
      apiError(freshOrderResult.error);
      return rejectWithValue(freshOrderResult.error);
    }

    // Online: use the Supabase RPC directly — a single atomic statement, so
    // there's no risk of the "insert settlement" and "update order status"
    // writes landing independently of each other. Offline: fall back to the
    // local writeTransaction path; the order-status trigger added alongside
    // this change (see migration 20260803150000) guarantees status still
    // gets derived correctly once the settlement row itself syncs up, even
    // if the paired local status-update CRUD op is ever lost.
    let settlementResponse: ApiResponse<boolean>;
    if (await isOnline()) {
      const rpcResponse = await directSettleOrder(
        selectedOrder.order_id,
        paymentMethodId,
        notes || undefined,
      );
      if (rpcResponse.success && rpcResponse.data) {
        await localSeedRpcSettlement(
          rpcResponse.data,
          selectedOrder.order_id,
          paymentMethodId,
        );
      }
      settlementResponse = rpcResponse.success
        ? { success: true, data: true }
        : { success: false, error: rpcResponse.error, data: false };
    } else {
      settlementResponse = await localSettleOrder(
        selectedOrder.order_id,
        freshOrderResult.order.net_amount,
        paymentMethodId,
        notes || null,
        ctx,
      );
    }

    if (!settlementResponse.success) {
      const message = settlementResponse.error || 'Failed to settle order';
      apiError(message);
      return rejectWithValue(message);
    }

    await submitSettledSaleToEbm(state, selectedOrder.order_id);

    const outletId = state.auth.user?.outlet?.id;
    if (outletId) {
      dispatch(fetchTables(outletId));
      dispatch(fetchTakeAwayOrdersForOutlet(outletId));
    }

    apiSuccess('Order settled successfully');

    return {
      order: freshOrderResult.order,
      items: state.runningOrder.selectedOrderItems,
      settlements: [
        {
          payment_method_name: resolveSettlementDisplayName(
            state,
            paymentMethodId,
          ),
          amount: freshOrderResult.order.net_amount,
        },
      ],
    };
  },
);

export const submitSplitSettlementForSelectedOrder = createAsyncThunk<
  SettlementReceiptContext,
  SplitSettlementPayload,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/submitSplitSettlementForSelectedOrder',
  async ({ notes, payments }, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!selectedOrder) {
      const message = 'No selected order';
      apiError(message);
      return rejectWithValue(message);
    }

    const fiscalBlockMessage = await assertFiscalReadiness(
      state,
      selectedOrder.order_id,
    );
    if (fiscalBlockMessage) {
      apiError(fiscalBlockMessage);
      return rejectWithValue(fiscalBlockMessage);
    }

    const ctx = buildLocalCtx(state);
    const freshOrderResult = await getFreshOrderForAction(
      selectedOrder.order_id,
      'settling the order',
      ctx,
    );

    if (!freshOrderResult.success) {
      apiError(freshOrderResult.error);
      return rejectWithValue(freshOrderResult.error);
    }

    const localPayments = payments
      .filter((payment) => payment.amount > 0)
      .map((payment) => ({
        paymentMethodId: payment.paymentMethodId,
        amount: payment.amount,
      }));

    if (localPayments.length === 0) {
      const message = 'Enter at least one split payment amount';
      apiError(message);
      return rejectWithValue(message);
    }

    // Online: call split_settlement directly (single atomic statement).
    // Offline: local writeTransaction path, backstopped by the order-status
    // trigger (see directSettlement branch above for the full rationale).
    let splitResponse: ApiResponse<boolean>;
    if (await isOnline()) {
      const rpcResponse = await splitSettleOrder(
        selectedOrder.order_id,
        localPayments.map((p) => ({
          payment_method_id: p.paymentMethodId,
          amount: p.amount,
        })),
        notes || undefined,
      );
      if (
        rpcResponse.success &&
        rpcResponse.data &&
        rpcResponse.data.length > 0
      ) {
        // split_settlement's own status update uses the FIRST payment
        // method in the array (see migration 20260214213000), not the
        // largest amount like localSplitSettleOrder — match that here so
        // the locally-seeded status agrees with what the server actually set.
        await localSeedRpcSettlement(
          rpcResponse.data,
          selectedOrder.order_id,
          rpcResponse.data[0].payment_method_id,
        );
      }
      splitResponse = rpcResponse.success
        ? { success: true, data: true }
        : { success: false, error: rpcResponse.error, data: false };
    } else {
      splitResponse = await localSplitSettleOrder(
        selectedOrder.order_id,
        localPayments,
        notes || null,
        ctx,
      );
    }

    if (!splitResponse.success) {
      const message =
        splitResponse.error || 'Failed to save split settlement payments';
      apiError(message);
      return rejectWithValue(message);
    }

    await submitSettledSaleToEbm(state, selectedOrder.order_id);

    const outletId = state.auth.user?.outlet?.id;
    if (outletId) {
      dispatch(fetchTables(outletId));
      dispatch(fetchTakeAwayOrdersForOutlet(outletId));
    }

    apiSuccess('Split settlement saved');

    return {
      order: freshOrderResult.order,
      items: state.runningOrder.selectedOrderItems,
      settlements: localPayments.map((payment) => ({
        payment_method_name: resolveSettlementDisplayName(
          state,
          payment.paymentMethodId,
        ),
        amount: payment.amount,
      })),
    };
  },
);

export const submitCreditSettlementForSelectedOrder = createAsyncThunk<
  SettlementReceiptContext,
  CreditSettlementPayload,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/submitCreditSettlementForSelectedOrder',
  async ({ clientId, notes }, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!selectedOrder) {
      const message = 'No selected order';
      apiError(message);
      return rejectWithValue(message);
    }

    if (!clientId) {
      const message = 'Select a client for credit settlement';
      apiError(message);
      return rejectWithValue(message);
    }

    const fiscalBlockMessage = await assertFiscalReadiness(
      state,
      selectedOrder.order_id,
    );
    if (fiscalBlockMessage) {
      apiError(fiscalBlockMessage);
      return rejectWithValue(fiscalBlockMessage);
    }

    const ctx = buildLocalCtx(state);
    const freshOrderResult = await getFreshOrderForAction(
      selectedOrder.order_id,
      'settling the order',
      ctx,
    );

    if (!freshOrderResult.success) {
      apiError(freshOrderResult.error);
      return rejectWithValue(freshOrderResult.error);
    }

    // Online: call company_settlement directly (single atomic statement).
    // Offline: local writeTransaction path, backstopped by the order-status
    // trigger (see directSettlement branch above for the full rationale).
    let response: ApiResponse<boolean>;
    if (await isOnline()) {
      const rpcResponse = await companySettleOrder(
        selectedOrder.order_id,
        clientId,
        notes || undefined,
      );
      if (rpcResponse.success && rpcResponse.data) {
        await localSeedRpcSettlement(
          rpcResponse.data,
          selectedOrder.order_id,
          rpcResponse.data.payment_method_id,
          clientId,
        );
      }
      response = rpcResponse.success
        ? { success: true, data: true }
        : { success: false, error: rpcResponse.error, data: false };
    } else {
      response = await localCreditSettleOrder(
        selectedOrder.order_id,
        freshOrderResult.order.net_amount,
        notes || null,
        ctx,
        clientId,
      );
    }

    if (!response.success) {
      const message = response.error || 'Failed to save credit settlement';
      apiError(message);
      return rejectWithValue(message);
    }

    await submitSettledSaleToEbm(state, selectedOrder.order_id);

    const outletId = state.auth.user?.outlet?.id;
    if (outletId) {
      dispatch(fetchTables(outletId));
      dispatch(fetchTakeAwayOrdersForOutlet(outletId));
    }

    apiSuccess('Credit settlement saved');

    return {
      order: freshOrderResult.order,
      items: state.runningOrder.selectedOrderItems,
      settlements: [
        {
          payment_method_name: resolveCreditSettlementDisplayName(state),
          amount: freshOrderResult.order.net_amount,
        },
      ],
    };
  },
);

export const submitOtherSettlementForSelectedOrder = createAsyncThunk<
  SettlementReceiptContext,
  OtherSettlementPayload,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/submitOtherSettlementForSelectedOrder',
  async ({ notes, type }, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!selectedOrder) {
      const message = 'No selected order';
      apiError(message);
      return rejectWithValue(message);
    }

    if (!notes.trim()) {
      const message = 'Settlement notes are required';
      apiError(message);
      return rejectWithValue(message);
    }

    const fiscalBlockMessage = await assertFiscalReadiness(
      state,
      selectedOrder.order_id,
    );
    if (fiscalBlockMessage) {
      apiError(fiscalBlockMessage);
      return rejectWithValue(fiscalBlockMessage);
    }

    const ctx = buildLocalCtx(state);
    const freshOrderResult = await getFreshOrderForAction(
      selectedOrder.order_id,
      'settling the order',
      ctx,
    );

    if (!freshOrderResult.success) {
      apiError(freshOrderResult.error);
      return rejectWithValue(freshOrderResult.error);
    }

    // Online: call other_settlement directly (single atomic statement).
    // Offline: local writeTransaction path, backstopped by the order-status
    // trigger (see directSettlement branch above for the full rationale).
    let response: ApiResponse<boolean>;
    if (await isOnline()) {
      const rpcResponse = await otherSettleOrder(
        selectedOrder.order_id,
        type,
        notes || undefined,
      );
      if (rpcResponse.success && rpcResponse.data) {
        await localSeedRpcSettlement(
          rpcResponse.data,
          selectedOrder.order_id,
          rpcResponse.data.payment_method_id,
        );
      }
      response = rpcResponse.success
        ? { success: true, data: true }
        : { success: false, error: rpcResponse.error, data: false };
    } else {
      response = await localOtherSettleOrder(
        selectedOrder.order_id,
        freshOrderResult.order.net_amount,
        type,
        notes || null,
        ctx,
      );
    }
    if (!response.success) {
      const message = response.error || 'Failed to save settlement';
      apiError(message);
      return rejectWithValue(message);
    }

    await submitSettledSaleToEbm(state, selectedOrder.order_id);

    const outletId = state.auth.user?.outlet?.id;
    if (outletId) {
      dispatch(fetchTables(outletId));
      dispatch(fetchTakeAwayOrdersForOutlet(outletId));
    }

    apiSuccess(`${type} settlement saved`);

    return {
      order: freshOrderResult.order,
      items: state.runningOrder.selectedOrderItems,
      settlements: [
        {
          payment_method_name: type,
          amount: freshOrderResult.order.net_amount,
        },
      ],
    };
  },
);

/**
 * Print a settlement receipt for the just-settled order, using the same BILL
 * printer mapping the pre-payment bill printed from. Fired from the
 * "Print a receipt?" prompt shown after any of the 4 settlement thunks above
 * resolve — reads its context from `pendingReceiptContext` rather than
 * `selectedOrder`, since settlement's own `.fulfilled` reducer clears
 * `selectedOrder`/`selectedOrderItems` as part of the post-settlement UX.
 */
export const confirmPrintReceiptForSettlement = createAsyncThunk<
  void,
  void,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/confirmPrintReceiptForSettlement',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const context = state.runningOrder.pendingReceiptContext;

    if (!context) {
      const message = 'No settlement to print a receipt for';
      apiError(message);
      return rejectWithValue(message);
    }

    if (!isElectron()) {
      const message = 'Printing is only available in the desktop app.';
      apiError(message);
      return rejectWithValue(message);
    }

    const authOutlet = state.auth.user?.outlet ?? null;
    const useTakeAwayPrinter = isTakeAwayLikeOrder(context.order);

    const printerResult = useTakeAwayPrinter
      ? resolveTakeAwayPrinter(state, 'BILL')
      : resolveSectionPrinter(state, context.order.section_id, 'BILL');
    if (!printerResult.success) {
      apiError(printerResult.error);
      return rejectWithValue(printerResult.error);
    }

    const billItems = getBillLineItems(context.items);
    const clientDetails = await getBillClientDetails(context.order);

    const printResponse = await printBillTicket({
      printer: {
        name: printerResult.printer.name,
        connection_type: printerResult.printer.connection_type,
        printer_identifier: printerResult.printer.printer_identifier,
        ip_address: printerResult.printer.ip_address,
        port: printerResult.printer.port,
        paper_size: printerResult.printer.paper_size,
        details: printerResult.printer.details,
      },
      payload: {
        outlet_name: context.order.outlet_name,
        outlet_address: authOutlet?.address ?? null,
        outlet_tin: authOutlet?.tin_number ?? null,
        outlet_contact: authOutlet?.phone ?? null,
        bill_number: context.order.bill_number,
        table_name:
          context.order.table_name || (useTakeAwayPrinter ? 'ORDER' : '-'),
        section_name:
          context.order.section_name || (useTakeAwayPrinter ? 'TAKEAWAY' : '-'),
        waiter_name: context.order.waiter_name || '-',
        printed_by: getPrintedByName(state),
        printed_at: new Date().toISOString(),
        is_receipt: true,
        items: billItems,
        client: clientDetails,
        total_amount: context.order.total_amount,
        discount_amount: context.order.discount_amount,
        net_amount: context.order.net_amount,
        settlements: context.settlements,
        footer_lines: [
          ...(authOutlet?.receipt_footnote
            ? [authOutlet.receipt_footnote]
            : []),
          'Powered by smartPOS Cloud',
          'www.smartposcloud.app',
        ],
      },
    });

    if (!printResponse.success) {
      const message = printResponse.error || 'Failed to print receipt';
      apiError(message);
      return rejectWithValue(message);
    }

    apiSuccess(
      `Receipt for Bill #${context.order.bill_number} sent to ${printerResult.printer.name}`,
    );

    return undefined;
  },
);

export const openOrderShiftModalForSelectedOrder = createAsyncThunk<
  SalesOrder,
  void,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/openOrderShiftModalForSelectedOrder',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!hasAccessControl(state, AccessControlCode.SALES_ORDER_ITEMS_SHIFT)) {
      const message = 'You do not have permission to shift orders';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (!selectedOrder) {
      const message = 'Select or create an order first';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (!isRestaurantOutlet(state)) {
      const message = 'Order shifting is only available for restaurant outlets';
      apiError(message);
      return rejectWithValue(message);
    }

    if (selectedOrder.mode !== 'DINE' || !selectedOrder.table_id) {
      const message = 'Only dining orders can be shifted';
      apiError(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const freshOrderResponse = await localFetchOrderById(
      selectedOrder.order_id,
      ctx,
    );
    if (!freshOrderResponse.success || !freshOrderResponse.data) {
      const message =
        freshOrderResponse.error || 'Failed to fetch latest order details';
      apiError(message);
      return rejectWithValue(message);
    }

    return freshOrderResponse.data;
  },
);

export const openOrderSplitModalForSelectedOrder = createAsyncThunk<
  SalesOrder,
  void,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/openOrderSplitModalForSelectedOrder',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!hasAccessControl(state, AccessControlCode.SALES_ORDER_ITEMS_SHIFT)) {
      const message = 'You do not have permission to split orders';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (!selectedOrder) {
      const message = 'Select or create an order first';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (!isRestaurantOutlet(state)) {
      const message = 'Order shifting is only available for restaurant outlets';
      apiError(message);
      return rejectWithValue(message);
    }

    if (selectedOrder.mode !== 'DINE' || !selectedOrder.table_id) {
      const message = 'Only dining orders can be shifted';
      apiError(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const freshOrderResponse = await localFetchOrderById(
      selectedOrder.order_id,
      ctx,
    );
    if (!freshOrderResponse.success || !freshOrderResponse.data) {
      const message =
        freshOrderResponse.error || 'Failed to fetch latest order details';
      apiError(message);
      return rejectWithValue(message);
    }

    return freshOrderResponse.data;
  },
);

export const submitOrderShiftForSelectedOrder = createAsyncThunk<
  RunningOrderShiftResult,
  ShiftOrderPayload,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/submitOrderShiftForSelectedOrder',
  async ({ targetTableId }, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!hasAccessControl(state, AccessControlCode.SALES_ORDER_ITEMS_SHIFT)) {
      const message = 'You do not have permission to shift orders';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (!selectedOrder) {
      const message = 'No selected order';
      apiError(message);
      return rejectWithValue(message);
    }

    if (!isRestaurantOutlet(state)) {
      const message = 'Order shifting is only available for restaurant outlets';
      apiError(message);
      return rejectWithValue(message);
    }

    if (selectedOrder.mode !== 'DINE' || !selectedOrder.table_id) {
      const message = 'Only dining orders can be shifted';
      apiError(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const response = await localShiftOrderToTable(
      selectedOrder.order_id,
      targetTableId,
    );
    if (!response.success || !response.data) {
      const message = response.error || 'Failed to shift order';
      apiError(message);
      return rejectWithValue(message);
    }

    const refreshedContext = await refreshOrderContext(
      selectedOrder.order_id,
      ctx,
      buildNameMap(state.runningOrder.selectedOrderItems),
    );
    if (!refreshedContext.success) {
      apiError(refreshedContext.error);
      return rejectWithValue(refreshedContext.error);
    }

    const outletId = state.auth.user?.outlet?.id;
    if (outletId) {
      dispatch(fetchTables(outletId));
      dispatch(fetchTakeAwayOrdersForOutlet(outletId));
    }

    apiSuccess('Order shifted successfully');

    return {
      order: refreshedContext.order,
      items: refreshedContext.items,
    };
  },
);

export const shiftSelectedConfirmedOrderItem = createAsyncThunk<
  RunningOrderShiftResult,
  ShiftOrderItemPayload,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/shiftSelectedConfirmedOrderItem',
  async (
    { orderItemId, targetTableId },
    { dispatch, getState, rejectWithValue },
  ) => {
    const state = getState();
    const { selectedOrder, selectedOrderItems } = state.runningOrder;

    if (!hasAccessControl(state, AccessControlCode.SALES_ORDER_ITEMS_SHIFT)) {
      const message = 'You do not have permission to shift order items';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (!selectedOrder) {
      const message = 'No selected order';
      apiError(message);
      return rejectWithValue(message);
    }

    if (!isRestaurantOutlet(state)) {
      const message = 'Item shifting is only available for restaurant outlets';
      apiError(message);
      return rejectWithValue(message);
    }

    if (selectedOrder.mode !== 'DINE' || !selectedOrder.table_id) {
      const message = 'Only dining confirmed items can be shifted';
      apiError(message);
      return rejectWithValue(message);
    }

    const targetItem = selectedOrderItems.find(
      (item) => item.id === orderItemId,
    );
    if (!targetItem) {
      const message = 'Order item not found';
      apiError(message);
      return rejectWithValue(message);
    }

    if (targetItem.status !== 'CONFIRMED') {
      const message = 'Only confirmed items can be shifted';
      apiError(message);
      return rejectWithValue(message);
    }

    const response = await shiftConfirmedOrderItemToTable(
      orderItemId,
      targetTableId,
    );
    if (!response.success || !response.data) {
      const message = response.error || 'Failed to shift order item';
      apiError(message);
      return rejectWithValue(message);
    }

    // This RPC writes straight to Supabase (Category B) — PowerSync's local
    // SQLite mirror has not caught up yet, so re-reading the source order
    // from local SQLite here would still show the shifted item present (a
    // read-your-own-write race). We already know, from the RPC's own
    // success, that targetItem left this order — remove it from Redux state
    // directly instead of trusting an immediate local re-read. The
    // usePowerSyncChange(['order_items']) listener in RunningOrderLiveSync.tsx
    // self-corrects once the local mirror does catch up, as a backstop in
    // case this optimistic update is ever wrong (e.g. a concurrent edit
    // raced it).
    const remainingItems = selectedOrderItems.filter(
      (item) => item.id !== orderItemId,
    );
    const shiftedAmount = targetItem.amount;
    const newTotalAmount = selectedOrder.total_amount - shiftedAmount;
    const newNetAmount = newTotalAmount - selectedOrder.discount_amount;
    const newBalanceAmount =
      newNetAmount - (selectedOrder.settlement_total ?? 0);
    const optimisticOrder: SalesOrder = {
      ...selectedOrder,
      total_amount: newTotalAmount,
      net_amount: newNetAmount,
      balance_amount: newBalanceAmount,
      item_count: Math.max(0, selectedOrder.item_count - 1),
    };

    const outletId = state.auth.user?.outlet?.id;
    if (outletId) {
      dispatch(fetchTables(outletId));
      dispatch(fetchTakeAwayOrdersForOutlet(outletId));
    }

    apiSuccess('Order item shifted successfully');

    return {
      order: optimisticOrder,
      items: remainingItems,
    };
  },
);

export const shiftSelectedConfirmedOrderItems = createAsyncThunk<
  RunningOrderShiftResult,
  ShiftOrderItemsPayload,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/shiftSelectedConfirmedOrderItems',
  async (
    { orderItemIds, targetTableId },
    { dispatch, getState, rejectWithValue },
  ) => {
    const state = getState();
    const { selectedOrder, selectedOrderItems } = state.runningOrder;

    if (!hasAccessControl(state, AccessControlCode.SALES_ORDER_ITEMS_SHIFT)) {
      const message = 'You do not have permission to shift order items';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (!selectedOrder) {
      const message = 'No selected order';
      apiError(message);
      return rejectWithValue(message);
    }

    if (!isRestaurantOutlet(state)) {
      const message = 'Item shifting is only available for restaurant outlets';
      apiError(message);
      return rejectWithValue(message);
    }

    if (selectedOrder.mode !== 'DINE' || !selectedOrder.table_id) {
      const message = 'Only dining confirmed items can be shifted';
      apiError(message);
      return rejectWithValue(message);
    }

    if (orderItemIds.length === 0) {
      const message = 'No order items selected';
      apiError(message);
      return rejectWithValue(message);
    }

    const targetItems = selectedOrderItems.filter((item) =>
      orderItemIds.includes(item.id),
    );
    if (targetItems.length !== orderItemIds.length) {
      const message = 'One or more selected items were not found';
      apiError(message);
      return rejectWithValue(message);
    }

    if (targetItems.some((item) => item.status !== 'CONFIRMED')) {
      const message = 'Only confirmed items can be shifted';
      apiError(message);
      return rejectWithValue(message);
    }

    const response = await shiftConfirmedOrderItemsToTable(
      orderItemIds,
      targetTableId,
    );
    if (!response.success || !response.data) {
      const message = response.error || 'Failed to shift order items';
      apiError(message);
      return rejectWithValue(message);
    }

    // Same read-your-own-write reasoning as shiftSelectedConfirmedOrderItem
    // above — optimistically remove the whole shifted selection from Redux
    // instead of re-reading local SQLite immediately after this Category B
    // RPC write. usePowerSyncChange(['order_items']) in
    // RunningOrderLiveSync.tsx self-corrects once the local mirror catches
    // up, applying equally here since it's table-level, not per-item.
    const shiftedIds = new Set(orderItemIds);
    const remainingItems = selectedOrderItems.filter(
      (item) => !shiftedIds.has(item.id),
    );
    const shiftedAmount = targetItems.reduce(
      (sum, item) => sum + item.amount,
      0,
    );
    const newTotalAmount = selectedOrder.total_amount - shiftedAmount;
    const newNetAmount = newTotalAmount - selectedOrder.discount_amount;
    const newBalanceAmount =
      newNetAmount - (selectedOrder.settlement_total ?? 0);
    const optimisticOrder: SalesOrder = {
      ...selectedOrder,
      total_amount: newTotalAmount,
      net_amount: newNetAmount,
      balance_amount: newBalanceAmount,
      item_count: Math.max(0, selectedOrder.item_count - targetItems.length),
    };

    const outletId = state.auth.user?.outlet?.id;
    if (outletId) {
      dispatch(fetchTables(outletId));
      dispatch(fetchTakeAwayOrdersForOutlet(outletId));
    }

    apiSuccess(
      targetItems.length === 1
        ? 'Order item shifted successfully'
        : `${targetItems.length} order items shifted successfully`,
    );

    return {
      order: optimisticOrder,
      items: remainingItems,
    };
  },
);

export const refreshSelectedOrderContext = createAsyncThunk<
  { order: SalesOrder; items: SalesOrderItem[] },
  void,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/refreshSelectedOrderContext',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder } = state.runningOrder;

    if (!selectedOrder) {
      return rejectWithValue('No selected order');
    }

    const ctx = buildLocalCtx(state);
    const refreshedContext = await refreshOrderContext(
      selectedOrder.order_id,
      ctx,
      buildNameMap(state.runningOrder.selectedOrderItems),
    );
    if (!refreshedContext.success) {
      // The order is gone from local SQLite entirely (e.g. deleted by a
      // cross-device conflict resolution) — let the user know their screen
      // is about to clear, rather than leaving them to discover it via a
      // raw "Order not found" error on their next click.
      apiInfo('This order was resolved on another device.');
      return rejectWithValue(refreshedContext.error);
    }

    return {
      order: refreshedContext.order,
      items: refreshedContext.items,
    };
  },
);

export const updateSelectedOrderItemNotes = createAsyncThunk<
  { order: SalesOrder; items: SalesOrderItem[] },
  UpdateOrderItemNotesPayload,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/updateSelectedOrderItemNotes',
  async ({ notes, orderItemId }, { getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder, selectedOrderItems } = state.runningOrder;

    if (!selectedOrder) {
      const message = 'No selected order';
      apiError(message);
      return rejectWithValue(message);
    }

    const targetItem = selectedOrderItems.find(
      (item) => item.id === orderItemId,
    );
    if (!targetItem) {
      const message = 'Order item not found';
      apiError(message);
      return rejectWithValue(message);
    }

    if (targetItem.status !== 'PENDING') {
      const message = 'Only pending items can have notes updated';
      apiError(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const response = await localUpdateOrderItemNotes(orderItemId, notes);
    if (!response.success) {
      const message = response.error || 'Failed to update order item notes';
      apiError(message);
      return rejectWithValue(message);
    }

    const refreshedContext = await refreshOrderContext(
      selectedOrder.order_id,
      ctx,
      buildNameMap(state.runningOrder.selectedOrderItems),
    );
    if (!refreshedContext.success) {
      apiError(refreshedContext.error);
      return rejectWithValue(refreshedContext.error);
    }

    apiSuccess('Order item notes updated');

    return {
      order: refreshedContext.order,
      items: refreshedContext.items,
    };
  },
);

export const removeSelectedOrderItem = createAsyncThunk<
  { order: SalesOrder; items: SalesOrderItem[] },
  RemoveOrderItemPayload,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/removeSelectedOrderItem',
  async ({ orderItemId, reason }, { getState, rejectWithValue }) => {
    const state = getState();
    const { selectedOrder, selectedOrderItems } = state.runningOrder;

    if (!selectedOrder) {
      const message = 'No selected order';
      apiError(message);
      return rejectWithValue(message);
    }

    const targetItem = selectedOrderItems.find(
      (item) => item.id === orderItemId,
    );
    if (!targetItem) {
      const message = 'Order item not found';
      apiError(message);
      return rejectWithValue(message);
    }

    if (
      targetItem.status === 'PENDING' &&
      !hasAccessControl(
        state,
        AccessControlCode.SALES_ORDER_ITEMS_DELETE_PENDING,
      )
    ) {
      const message =
        'You do not have permission to delete pending order items';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (
      targetItem.status === 'CONFIRMED' &&
      !hasAccessControl(state, AccessControlCode.SALES_ORDER_ITEMS_CANCEL)
    ) {
      const message = 'You do not have permission to cancel confirmed items';
      apiInfo(message);
      return rejectWithValue(message);
    }

    if (targetItem.status === 'CONFIRMED' && !reason?.trim()) {
      const message = 'Cancellation reason is required for confirmed items';
      apiError(message);
      return rejectWithValue(message);
    }

    const ctx = buildLocalCtx(state);
    const response = await localRemoveOrderItem(orderItemId, reason, ctx);
    if (!response.success) {
      const message = response.error || 'Failed to remove order item';
      apiError(message);
      return rejectWithValue(message);
    }

    const refreshedContext = await refreshOrderContext(
      selectedOrder.order_id,
      ctx,
      buildNameMap(state.runningOrder.selectedOrderItems),
    );
    if (!refreshedContext.success) {
      apiError(refreshedContext.error);
      return rejectWithValue(refreshedContext.error);
    }

    const successMessage =
      targetItem.status === 'PENDING'
        ? 'Pending item deleted'
        : 'Confirmed item cancelled';

    apiSuccess(successMessage);

    return {
      order: refreshedContext.order,
      items: refreshedContext.items,
    };
  },
);

export const startAddItemFlow = createAsyncThunk<
  MenuItem,
  MenuItem,
  { state: RunningOrderThunkState; rejectValue: string }
>(
  'runningOrder/startAddItemFlow',
  async (item, { getState, rejectWithValue }) => {
    const state = getState();
    if (!state.runningOrder.selectedOrder) {
      const message = 'Select or create an order first';
      apiInfo(message);
      return rejectWithValue(message);
    }

    return item;
  },
);
